<script setup lang="ts">
import { EDate, EEmptyState } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/AppIcon.vue'
import type { TimelineEvent } from '@/composables/useRightRail'
import { useAuthStore } from '@/stores/auth'

/**
 * A record's history, newest first.
 *
 * §11 puts this in every document's right rail ("who did what, when"). The
 * events are always built from values the server returned — a timeline that
 * invents a step it did not observe is worse than no timeline at all — so a
 * record with only three known instants shows three entries and says so.
 */

const props = withDefaults(
  defineProps<{
    events: readonly TimelineEvent[]
    emptyTitle?: string
    emptyDescription?: string
  }>(),
  { emptyTitle: '', emptyDescription: '' },
)

const auth = useAuthStore()
const { t } = useI18n()

const heading = computed(() =>
  props.emptyTitle === '' ? t('shell.timeline.emptyTitle') : props.emptyTitle,
)
const blurb = computed(() =>
  props.emptyDescription === '' ? t('shell.timeline.emptyDescription') : props.emptyDescription,
)
</script>

<template>
  <EEmptyState
    v-if="events.length === 0"
    compact
    :title="heading"
    :description="blurb"
  />

  <ol v-else class="timeline">
    <li v-for="event in events" :key="event.id" class="timeline__item">
      <span class="timeline__marker">
        <AppIcon :name="event.icon ?? 'clock'" :size="11" />
      </span>

      <div>
        <p class="timeline__label">{{ event.label }}</p>
        <p class="timeline__meta">
          <EDate :value="event.at" preset="dateTime" :time-zone="auth.timeZone" show-zone />
          <template v-if="event.actor"> · {{ event.actor }}</template>
        </p>
        <p v-if="event.description" class="timeline__meta">{{ event.description }}</p>
      </div>
    </li>
  </ol>
</template>
